import type {
  IfrsImportanceAssessment,
  OffBalanceAsset,
  SettlementDateCriteria,
  SmallLeaseJudgeCriteriaJapan,
} from '@libs/enums';
import type { ItemAttributes, ItemNames } from '@ps-smart-cp/ppfw-client';
import {
  Bool,
  Characters,
  ItemName,
  Locale,
  Numeric,
  type OrEmpty,
  SingleDate,
  SingleValue,
} from '@ps-smart-cp/ppfw-client';

interface Schema {
  // 期首年月
  readonly beginningYearMonth: Date | null;

  // 決算日基準
  readonly settlementDateBasis: SettlementDateCriteria;

  // 決算日
  readonly settlementDate: string;

  // 表示通貨
  readonly displayCurrency: string;

  // 金額項目小数部有効桁数
  readonly currencyValidNoDecimalPlaces: number;

  // 再リースに伴う除却
  readonly isRetireReLease: boolean;

  // 所有権移転リースの除却
  readonly isOwnershipRightsTransferLeaseDisposal: boolean;

  // 適用利子率上限値
  readonly maximumAppliedInterestRate: string;

  // 重要性判定のリース料総額
  readonly imporJudgeLeaseFeeTotalAmt: string;

  // オフバランスの資産管理区分
  readonly offBalanceFaControlClass: OrEmpty<OffBalanceAsset>;

  // 少額リース判定基準(日本基準)
  readonly smallLeaseJudgeBasisJapan: SmallLeaseJudgeCriteriaJapan;

  // 重要性判定のリース料総額(日本基準)
  readonly imporJudgeTotalLeaseFeeJapan: string;

  // 自動判定の見積現金購入額(少額リース)
  readonly autoJudgeEstCashPurchaseAmountSmall: string;

  // 重要性判定(IFRS基準)
  readonly imporJudgeIfrsBasis: IfrsImportanceAssessment;

  // 重要性判定のリース料総額(IFRS基準)
  readonly imporJudgeTotalLeaseFeeIfrs: string;
}

const attributes = {
  beginningYearMonth: SingleDate.ym(true),
  settlementDateBasis: SingleValue.longValue(),
  settlementDate: Numeric.of(1, 31, 0),
  displayCurrency: Characters.preset.halfWidth({ maxLen: 3 }),
  // TODO https://proship-infra.atlassian.net/browse/PSD-234 一時的に最小値を0,最大値を5に設定する。
  currencyValidNoDecimalPlaces: Numeric.of(0, 5, 0, true),
  isRetireReLease: Bool.new(),
  isOwnershipRightsTransferLeaseDisposal: Bool.new(),
  maximumAppliedInterestRate: Numeric.of(0, 1, 7, true),
  smallLeaseJudgeBasisJapan: SingleValue.shortValue(),
  imporJudgeLeaseFeeTotalAmt: Numeric.preset.currency(undefined, true),
  imporJudgeTotalLeaseFeeJapan: Numeric.preset.currency(undefined, true),
  autoJudgeEstCashPurchaseAmountSmall: Numeric.preset.currency(),
  offBalanceFaControlClass: SingleValue.shortValue(true),
  imporJudgeIfrsBasis: SingleValue.shortValue(),
  imporJudgeTotalLeaseFeeIfrs: Numeric.preset.currency(),
} satisfies ItemAttributes<Schema>;

const names = {
  beginningYearMonth: ItemName.of([Locale.ja, '期首年月'], [Locale.en, 'Begin Term Date']),
  settlementDateBasis: ItemName.of([Locale.ja, '決算日基準'], [Locale.en, 'Settlement Date Basis']),
  settlementDate: ItemName.of([Locale.ja, '決算日'], [Locale.en, 'Settlement Date']),
  displayCurrency: ItemName.of([Locale.ja, '表示通貨']),
  currencyValidNoDecimalPlaces: ItemName.of([Locale.ja, '金額項目小数部有効桁数']),
  isRetireReLease: ItemName.of([Locale.ja, '再リースに伴う除却'], [Locale.en, 'Disposal For ReLease']),
  isOwnershipRightsTransferLeaseDisposal: ItemName.of(
    [Locale.ja, '所有権移転リースの除却'],
    [Locale.en, 'Ownership Rights Transfer Lease Disposal'],
  ),
  maximumAppliedInterestRate: ItemName.of(
    [Locale.ja, '適用利子率上限値'],
    [Locale.en, 'Maximum Applied Interest Rate'],
  ),
  smallLeaseJudgeBasisJapan: ItemName.of([Locale.ja, '少額リース判定基準(日本基準)']),
  imporJudgeLeaseFeeTotalAmt: ItemName.of(
    [Locale.ja, '重要性判定のリース料総額'],
    [Locale.en, 'Importance Judge Lease Fee Total Amount'],
  ),
  imporJudgeTotalLeaseFeeJapan: ItemName.of([Locale.ja, '重要性判定のリース料総額(日本基準)']),
  autoJudgeEstCashPurchaseAmountSmall: ItemName.of(
    [Locale.ja, '自動判定の見積現金購入額(少額リース)'],
    [Locale.en, 'Estimated Cash Purchase Amount For Auto Judgment'],
  ),
  offBalanceFaControlClass: ItemName.of(
    [Locale.ja, 'オフバランスの資産管理区分'],
    [Locale.en, 'Off Balance Asset Management Class'],
  ),
  imporJudgeIfrsBasis: ItemName.of(
    [Locale.ja, '重要性判定(IFRS基準)'],
    [Locale.en, 'Importance Judge For IFRS Basis'],
  ),
  imporJudgeTotalLeaseFeeIfrs: ItemName.of(
    [Locale.ja, '重要性判定のリース料総額(IFRS基準)'],
    [Locale.en, 'Importance Judge Lease Fee Total Amount For IFRS Basis'],
  ),
} satisfies ItemNames<Schema>;

const titleNames = {
  accountingPolicySettingTitle: ItemName.of(
    [Locale.ja, '会計方針設定-会計方針設定'],
    [Locale.en, 'Accounting Policy Settings-Accounting Policy Setting'],
  ),
  leaseAssetSettingTitle: ItemName.of(
    [Locale.ja, '会計方針設定-リース資産の設定'],
    [Locale.en, 'Accounting Policy Settings-Lease Asset Setting'],
  ),
  basicInfo: ItemName.of([Locale.ja, '会計方針設定'], [Locale.en, 'Accounting Policy Settings']),
  laContractSettings: ItemName.of([Locale.ja, 'リース契約の設定']),
  leaseAssetSetting: ItemName.of([Locale.ja, 'リース資産の設定'], [Locale.en, 'Lease Asset Setting']),
};

const labelNames = {
  carryoverProcessing: ItemName.of(
    [Locale.ja, '繰越処理は締め状態を必ず本締めにする'],
    [Locale.en, 'Carryover Processing'],
  ),
  openingCarryover: ItemName.of(
    [Locale.ja, '期首繰越で資産単位に設定されている増加償却率を初期化する'],
    [Locale.en, 'Opening Carryover'],
  ),
};

interface Record {
  readonly beginningYearMonth: string;
  readonly settlementDateBasis: string;
  readonly settlementDate: string;
  readonly displayCurrency: string;
  readonly currencyValidNoDecimalPlaces: number;
  readonly isRetireReLease: boolean;
  readonly isOwnershipRightsTransferLeaseDisposal: boolean;
  readonly maximumAppliedInterestRate: string;
  readonly smallLeaseJudgeBasisJapan: string;
  readonly imporJudgeLeaseFeeTotalAmt: string;
  readonly imporJudgeTotalLeaseFeeJapan: string;
  readonly autoJudgeEstCashPurchaseAmountSmall: string;
  readonly offBalanceFaControlClass: string;
  readonly imporJudgeIfrsBasis: string;
  readonly imporJudgeTotalLeaseFeeIfrs: string;
}

const accountingPolicy = {
  names,
  labelNames,
  titleNames,
  attributes,
};

interface AccountingPolicy {
  schema: Schema;
  record: Record;
}

export type { AccountingPolicy };
export { accountingPolicy };
